import type { FC, ReactNode } from 'react'

import { Fragment, useRef } from 'react'
import { ArrowRight } from 'lucide-react'
import { gsap, MOTION, useGSAP } from '@/lib/motion'
import { SplitWords } from '@/components/generals/split-words'

const READ = "We love creating beautiful designs for you, of course. However, our primary focus is working with you to help your business succeed. If that means getting you more traffic to your website, we're here. If it means creating a website that converts browsers into customers, we will do that."

const EMPHASIS = new Set(['succeed.', 'traffic', 'customers,'])

/** Chapter four, on mist: a paragraph that inks itself in as it is read, then a promise that grows as big as the goals. */
export const DifferentSection: FC = (): ReactNode => {
    const ref = useRef<HTMLElement>(null)

    useGSAP(() => {
        const section = ref.current

        if (!section) return

        const q = gsap.utils.selector(section)
        const mm = gsap.matchMedia()

        mm.add(MOTION.ok, () => {
            gsap.fromTo(q('[data-read-word]'), { opacity: 0.16 }, {
                opacity: 1,
                ease: 'none',
                stagger: 0.1,
                scrollTrigger: { trigger: q('[data-read]')[0], start: 'top 78%', end: 'bottom 42%', scrub: 0.4 }
            })

            gsap.fromTo(q('[data-mark]'), { scaleX: 0 }, {
                scaleX: 1,
                ease: 'none',
                stagger: 0.4,
                scrollTrigger: { trigger: q('[data-read]')[0], start: 'top 55%', end: 'bottom 40%', scrub: 0.4 }
            })

            gsap.fromTo(q('[data-partner] [data-word]'), { yPercent: 110 }, {
                yPercent: 0,
                duration: 1.1,
                stagger: 0.035,
                ease: 'expo.out',
                scrollTrigger: { trigger: q('[data-partner]')[0], start: 'top 80%', toggleActions: 'play none none reverse' }
            })

            const big = gsap.timeline({
                scrollTrigger: { trigger: q('[data-goals]')[0], start: 'top 85%', end: 'center 45%', scrub: 0.5 }
            })

            big
                .fromTo(q('[data-big]'), { '--wdth': 62, '--big': 1 }, { '--wdth': 125, '--big': 1.6, ease: 'power2.inOut', duration: 1 }, 0)
                .fromTo(q('[data-big-fill]'), { scaleY: 0 }, { scaleY: 1, ease: 'power2.inOut', duration: 0.6 }, 0.25)
        })

        return () => mm.revert()
    }, { scope: ref })

    const words = READ.split(' ')

    return (
        <section
            ref={ref}
            id='different'
            data-tone='mist'
            data-surface='light'
            aria-labelledby='different-title'
            className='relative bg-mist px-(--gutter) pb-[16vh] pt-[18vh] text-ink'
        >
            <div className='grid gap-x-8 gap-y-10 md:grid-cols-12'>
                <div className='md:col-span-3'>
                    <h2 id='different-title' className='label md:sticky md:top-28'>
                        <span className='mr-3'>02</span>
                        What makes us different?
                    </h2>
                </div>

                <div className='md:col-span-9'>
                    <p
                        data-read=''
                        className='text-[clamp(1.65rem,3.4vw,3.6rem)] font-medium leading-[1.1] tracking-[-0.025em] wdth-87'
                    >
                        {words.map((word, index) => (
                            <Fragment key={`${word}-${index}`}>
                                <span data-read-word='' className='relative isolate inline-block'>
                                    {EMPHASIS.has(word) && (
                                        <span
                                            data-mark=''
                                            aria-hidden='true'
                                            className='absolute inset-x-[-0.06em] bottom-[0.06em] top-[0.55em] -z-10 origin-left bg-signal'
                                        />
                                    )}
                                    {word}
                                </span>
                                {index < words.length - 1 ? ' ' : null}
                            </Fragment>
                        ))}
                    </p>

                    <a
                        href='#examples'
                        className='group mt-12 inline-flex items-center gap-3 border-b border-ink pb-1.5 text-base font-semibold'
                    >
                        View website design examples
                        <ArrowRight aria-hidden='true' className='size-4 transition-transform duration-300 group-hover:translate-x-1' />
                    </a>
                </div>
            </div>

            <div className='mt-[20vh] grid gap-x-8 md:grid-cols-12'>
                <p
                    data-partner=''
                    className='display text-[clamp(2.3rem,5.6vw,6.6rem)] leading-[0.95] wdth-87 md:col-span-11'
                >
                    <SplitWords text="When you hire Localboost, you are bringing on a partner who cares about you and wants you to reach your business goals." />
                </p>

                <p
                    data-goals=''
                    className='display mt-[8vh] text-[clamp(2.3rem,5.6vw,6.6rem)] leading-[0.95] wdth-87 md:col-span-12 md:col-start-1 md:text-right'
                >
                    However{' '}
                    <span className='relative inline-block align-baseline'>
                        <span data-big-fill='' aria-hidden='true' className='absolute inset-x-[-0.08em] bottom-[0.02em] top-[0.12em] origin-bottom bg-signal' />
                        <span data-big='' className='relative inline-block text-[calc(var(--big,1)*1em)]'>big</span>
                    </span>{' '}
                    those goals may be.
                </p>
            </div>
        </section>
    )
}
